import type { AudioManager } from '../../audio/AudioManager';
import { WEAPONS } from '../../data/weapons';
import type { TouchInput } from '../../input/TouchInput';
import type {
  BattleOutcome,
  Enemy,
  Hero,
  Weapon,
} from '../../types';
import { makeOutcome } from '../systems/Combat';
import { pickNextAttack } from '../systems/EnemyGen';
import type { Phase } from '../Phase';
import { DefendSubphase, type DefendOutcome } from '../subphases/DefendSubphase';
import { KillWindowSubphase } from '../subphases/KillWindowSubphase';
import { drawCombatBackground } from '../../render/CombatBackground';
import { drawSilhouette, type SilhouetteId, worldHandAnchor } from '../../render/SilhouetteRenderer';
import { drawBlade } from '../../render/WeaponRenderer';
import { LOGICAL_WIDTH } from '../../render/Renderer';

type CombatStage =
  | { kind: 'intro'; t: number }
  | { kind: 'gap'; t: number; durSec: number }
  | { kind: 'defend'; sub: DefendSubphase }
  | { kind: 'killWindow'; sub: KillWindowSubphase }
  | { kind: 'finished' };

export class CombatPhase implements Phase {
  private hero: Hero;
  private enemy: Enemy;
  private weapon: Weapon;
  private input: TouchInput;
  private audio: AudioManager;
  private heroSilhouette: SilhouetteId;
  private enemySilhouette: SilhouetteId;
  private stage: CombatStage = { kind: 'intro', t: 0 };
  private outcome: BattleOutcome | null = null;
  /** Track which defense outcomes happened, in order, for telemetry / result display. */
  private defenseLog: DefendOutcome[] = [];
  private done = false;
  private idlePhase = 0;
  /** True after the last subphase has been disposed so we don't double-dispose. */
  private subDisposed = false;

  constructor(hero: Hero, enemy: Enemy, weapon: Weapon, input: TouchInput, audio: AudioManager) {
    this.hero = hero;
    this.enemy = enemy;
    this.weapon = weapon;
    this.input = input;
    this.audio = audio;
    this.heroSilhouette = hero.silhouetteId as SilhouetteId;
    this.enemySilhouette = enemy.silhouetteId as SilhouetteId;
  }

  update(dt: number): void {
    if (this.done) return;
    this.idlePhase += dt;
    const s = this.stage;
    switch (s.kind) {
      case 'intro': {
        s.t += dt;
        if (s.t >= 1.0) this.startNextRound(0.2);
        break;
      }
      case 'gap': {
        s.t += dt;
        if (s.t >= s.durSec) this.openNextStage();
        break;
      }
      case 'defend': {
        s.sub.update(dt);
        if (s.sub.isComplete()) {
          const out = s.sub.getOutcome();
          if (out) this.applyDefendOutcome(out);
          s.sub.dispose?.();
          this.subDisposed = true;
          if (this.checkLossConditions()) return;
          if (this.enemy.fatigue >= this.enemy.fatigueThreshold) {
            this.startKillWindow();
          } else {
            this.startNextRound(0.55);
          }
        }
        break;
      }
      case 'killWindow': {
        s.sub.update(dt);
        if (s.sub.isComplete()) {
          const out = s.sub.getOutcome();
          s.sub.dispose?.();
          this.subDisposed = true;
          if (out && out.hit) {
            this.recordVictory(out.quality === 'perfect' ? 'perfect' : 'good');
          } else {
            // Miss: enemy recovers, fatigue resets to 50%, back to defend.
            this.enemy.fatigue = Math.floor(this.enemy.fatigueThreshold / 2);
            this.startNextRound(0.5);
          }
        }
        break;
      }
      case 'finished':
        break;
    }
  }

  private startNextRound(gapSec: number): void {
    this.stage = { kind: 'gap', t: 0, durSec: gapSec };
  }

  private openNextStage(): void {
    if (this.outcome) {
      this.stage = { kind: 'finished' };
      this.done = true;
      return;
    }
    const pattern = pickNextAttack(this.enemy);
    const sub = new DefendSubphase(
      this.hero,
      this.enemy,
      this.weapon,
      pattern,
      this.heroSilhouette,
      this.enemySilhouette,
      this.input,
      this.audio,
    );
    this.subDisposed = false;
    this.stage = { kind: 'defend', sub };
  }

  private startKillWindow(): void {
    const def = WEAPONS[this.weapon.type];
    const sub = new KillWindowSubphase(
      this.hero,
      this.enemy,
      this.weapon,
      def,
      this.heroSilhouette,
      this.enemySilhouette,
      this.input,
      this.audio,
    );
    this.subDisposed = false;
    this.stage = { kind: 'killWindow', sub };
  }

  private applyDefendOutcome(out: DefendOutcome): void {
    this.defenseLog.push(out);
    this.hero.hp = Math.max(0, this.hero.hp - out.hpLoss);
    this.weapon.durability = Math.max(0, this.weapon.durability - out.durabilityLoss);
    this.enemy.fatigue = Math.min(
      this.enemy.fatigueThreshold,
      this.enemy.fatigue + out.fatigueGain,
    );
  }

  private checkLossConditions(): boolean {
    if (this.hero.hp <= 0) {
      this.recordOutcome('hero_death', null);
      return true;
    }
    if (this.weapon.durability <= 0) {
      this.recordOutcome('weapon_broke', null);
      return true;
    }
    return false;
  }

  private recordVictory(killQuality: 'perfect' | 'good'): void {
    this.weapon.battlesWon += 1;
    this.recordOutcome('victory', killQuality);
  }

  private recordOutcome(
    result: 'victory' | 'hero_death' | 'weapon_broke',
    killQuality: 'perfect' | 'good' | null,
  ): void {
    this.outcome = makeOutcome({
      result,
      weapon: this.weapon,
      hero: this.hero,
      enemy: this.enemy,
      killQuality,
    });
    this.stage = { kind: 'gap', t: 0, durSec: 0.6 };
  }

  isComplete(): boolean {
    return this.done;
  }

  getOutcome(): BattleOutcome | null {
    return this.outcome;
  }

  dispose(): void {
    if (this.subDisposed) return;
    const s = this.stage;
    if (s.kind === 'defend') s.sub.dispose?.();
    else if (s.kind === 'killWindow') s.sub.dispose?.();
    this.subDisposed = true;
  }

  render(ctx: CanvasRenderingContext2D): void {
    const s = this.stage;
    if (s.kind === 'defend') {
      s.sub.render(ctx);
      return;
    }
    if (s.kind === 'killWindow') {
      s.sub.render(ctx);
      return;
    }
    // intro / gap / finished — draw a calm scene with idle characters.
    drawCombatBackground(ctx, this.enemy.backgroundId);
    this.drawIdleCharacters(ctx);
    this.drawHud(ctx);
    if (s.kind === 'intro') this.drawIntroBanner(ctx, s.t);
  }

  private drawIdleCharacters(ctx: CanvasRenderingContext2D): void {
    const heroX = LOGICAL_WIDTH * 0.28;
    const enemyX = LOGICAL_WIDTH * 0.72;
    const feetY = 470;
    drawSilhouette(ctx, this.heroSilhouette, {
      cx: heroX,
      feetY,
      flip: false,
      swayPhase: this.idlePhase * 1.2,
    });
    const anchor = worldHandAnchor(this.heroSilhouette, heroX, feetY, false);
    drawBlade(ctx, this.weapon.shape, this.weapon.heat, {
      cx: anchor.x + 50,
      cy: anchor.y - 8,
      length: 92,
      rotation: anchor.rotation - 0.6,
      damage: this.weapon.damage,
    });
    drawSilhouette(ctx, this.enemySilhouette, {
      cx: enemyX,
      feetY,
      flip: true,
      swayPhase: this.idlePhase * 1.5 + 1.3,
    });
  }

  private drawIntroBanner(ctx: CanvasRenderingContext2D, t: number): void {
    const alpha = t < 0.2 ? t / 0.2 : t > 0.8 ? Math.max(0, (1 - t) / 0.2) : 1;
    ctx.fillStyle = `rgba(230, 211, 164, ${alpha})`;
    ctx.font = 'bold 22px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(this.enemy.displayName.toUpperCase(), LOGICAL_WIDTH / 2, 130);
    ctx.font = '12px system-ui, sans-serif';
    ctx.fillStyle = `rgba(168, 149, 122, ${alpha})`;
    ctx.fillText(`${this.hero.name} ${this.hero.title}`, LOGICAL_WIDTH / 2, 160);
  }

  private drawHud(ctx: CanvasRenderingContext2D): void {
    // Light HUD for between-round states.
    const heroPct = this.hero.hp / this.hero.maxHp;
    const fatPct = this.enemy.fatigue / this.enemy.fatigueThreshold;
    drawBar(ctx, 14, 14, 140, 12, heroPct, '#7fffaa', this.hero.name);
    drawBar(ctx, LOGICAL_WIDTH - 154, 14, 140, 12, fatPct, '#ffd060', this.enemy.displayName, true);
    const dPct = Math.max(0, this.weapon.durability) / 60;
    drawBar(ctx, 14, 32, 100, 6, dPct, '#c9b896', 'BLADE');
  }
}

function drawBar(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  pct: number,
  color: string,
  label: string,
  rightAlign = false,
): void {
  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  ctx.fillRect(x, y, w, h);
  ctx.fillStyle = color;
  ctx.fillRect(x + 1, y + 1, Math.max(0, (w - 2) * pct), h - 2);
  ctx.strokeStyle = '#3a3530';
  ctx.lineWidth = 1;
  ctx.strokeRect(x, y, w, h);
  ctx.fillStyle = '#c9b896';
  ctx.font = '10px system-ui, sans-serif';
  ctx.textAlign = rightAlign ? 'right' : 'left';
  ctx.textBaseline = 'top';
  ctx.fillText(label, rightAlign ? x + w : x, y + h + 2);
}
